import { z } from "zod";

import { accessorialSchema } from "./accessorial-schema.ts";

// Schema for contact details within an address book item
export const addressBookContactDetailSchema = z.object({
	contactDetailId: z.number(),
	addressBookItemId: z.number(),
	contactName: z.string().nullable(),
	contactPhone: z.string().nullable(),
	contactExtension: z.string().nullable(),
	hoursOpen: z.string().nullable(),
	hoursClose: z.string().nullable(),
	stopType: z.number(),
	mode: z.number(),
	timeZone: z.string().nullable(),
	specialInstructions: z.string().nullable(),
	accessorials: z.string().nullable(),
	rowVersion: z.string().nullable(),
});

// Schema for address book item summary response (v2 API)
export const addressBookItemSummarySchema = z.object({
	addressBookItemId: z.number(),
	companyName: z.string().nullable(),
	address1: z.string(),
	address2: z.string().nullable(),
	postalCode: z.string().nullable(),
	city: z.string().nullable(),
	stateProvince: z.string().nullable(),
	country: z.string(),
	createdDate: z.string(),
	createdByCustomerId: z.number().nullable(),
	createdByInternalId: z.number().nullable(),
	customerId: z.number(),
	isValidated: z.boolean().nullable(),
	legacyAddressId: z.number().nullable(),
	isLegacy: z.boolean(),
	rowVersion: z.string().nullable(),
	details: z.array(addressBookContactDetailSchema),
});

// Schema for full address book item details (includes location)
export const addressBookItemDetailsSchema = z.object({
	id: z.number(),
	contactName: z.string(),
	contactPhone: z.string(),
	contactExtension: z.string().nullable(),
	address1: z.string(),
	address2: z.string(),
	hoursOpen: z.string(),
	hoursClose: z.string(),
	stopType: z.string().nullable(),
	location: z.object({
		postalCode: z.string(),
		city: z.string(),
		state: z.string(),
		country: z.string(),
	}),
	accessorials: z.array(accessorialSchema).optional(),
	specialInstructions: z.string().optional(),
	isFavorite: z.boolean().optional(),
	isUserDefault: z.boolean().optional(),
});

// Schema for updating address book item
export const addressBookUpdateBodySchema = z.object({
	id: z.number(),
	stopType: z.string(),
	contactName: z.string(),
	contactPhone: z.string(),
	contactExtension: z.string().nullable(),
	address1: z.string(),
	address2: z.string(),
	hoursOpen: z.string(),
	hoursClose: z.string(),
	specialInstructions: z.string().optional(),
	location: z.object({
		postalCode: z.string(),
		city: z.string(),
		state: z.string(),
		country: z.string(),
	}),
});

export const addressBookCreateBodySchema = z.object({
	stopType: z.string().min(1),
	customerId: z.number().positive(),
	contactName: z.string().optional().default(""),
	contactPhone: z.string().min(1),
	contactExtension: z.string().optional().default(""),
	address1: z.string().min(1),
	address2: z.string().optional().default(""),
	postalCode: z.string().min(1),
	city: z.string().min(1),
	state: z.string().min(1),
	country: z.string().min(1),
	hoursOpen: z.string().min(1),
	hoursClose: z.string().min(1),
});

export const addressBookCreateResponseSchema = z.union([
	z.void(),
	z.null(),
	z.object({}).passthrough(),
]);

export type AddressBookItemSummary = z.infer<
	typeof addressBookItemSummarySchema
>;

export type AddressBookItemDetails = z.infer<
	typeof addressBookItemDetailsSchema
>;

export type AddressBookUpdateBody = z.infer<typeof addressBookUpdateBodySchema>;

export type AddressBookCreateBody = z.infer<typeof addressBookCreateBodySchema>;

// Schema for the query parameters
export const addressBookQueryParamsSchema = z.object({
	customerId: z.number(),
});

export type AddressBookQueryParams = z.infer<
	typeof addressBookQueryParamsSchema
>;

// Schema for filtered address book query parameters (shipment details)
export const addressBookFilteredQueryParamsSchema = z.object({
	customerId: z.number(),
	postalCode: z.string(),
	city: z.string(),
	state: z.string(),
	country: z.string(),
	stopType: z.enum(["shipper", "receiver"]),
});

export type AddressBookFilteredQueryParams = z.infer<
	typeof addressBookFilteredQueryParamsSchema
>;

export type AddressBookContactDetail = z.infer<
	typeof addressBookContactDetailSchema
>;

// Schema for contact details in v2 create request
export const addressBookContactDetailCreateSchema = z.object({
	contactName: z.string().optional(),
	contactPhone: z.string().optional(),
	contactExtension: z.string().nullable().optional(),
	hoursOpen: z.string().optional(),
	hoursClose: z.string().optional(),
	stopType: z.number().optional(),
	mode: z.number().optional(),
	timeZone: z.string().optional(),
	specialInstructions: z.string(),
	accessorials: z.string().optional(),
});

// Schema for v2 address book item creation request
export const addressBookCreateBodyV2Schema = z.object({
	customerId: z.number(),
	companyName: z.string().min(1),
	address1: z.string().min(1),
	address2: z.string().optional(),
	postalCode: z.string().min(1),
	city: z.string().min(1),
	stateProvince: z.string().min(1),
	country: z.string().min(1),
	isValidated: z.boolean().optional(),
	legacyAddressId: z.number().optional(),
	createdByCustomerId: z.number().optional(),
	details: z.array(addressBookContactDetailCreateSchema).optional().default([]),
});

export const addressBookCreateResponseV2Schema = z
	.object({
		addressBookItemId: z.number(),
	})
	.passthrough();

export type AddressBookCreateResponseV2 = z.infer<
	typeof addressBookCreateResponseV2Schema
>;

export type AddressBookCreateBodyV2 = z.infer<
	typeof addressBookCreateBodyV2Schema
>;

export type AddressBookContactDetailCreate = z.infer<
	typeof addressBookContactDetailCreateSchema
>;

// Schema for contact details in v2 update request
export const addressBookContactDetailUpdateSchema = z.object({
	contactDetailId: z.number().optional(),
	addressBookItemId: z.number().optional(),
	contactName: z.string().optional(),
	contactPhone: z.string().optional(),
	contactExtension: z.string().nullable().optional(),
	hoursOpen: z.string().optional(),
	hoursClose: z.string().optional(),
	stopType: z.number().optional(),
	mode: z.number().optional(),
	timeZone: z.string().optional(),
	specialInstructions: z.string().optional(),
	accessorials: z.string().optional(),
});

// Schema for v2 address book item update request
export const addressBookUpdateBodyV2Schema = z.object({
	addressBookItemId: z.number(),
	customerId: z.number(),
	companyName: z.string().optional(),
	address1: z.string(),
	address2: z.string().optional(),
	postalCode: z.string().optional(),
	city: z.string().optional(),
	stateProvince: z.string().optional(),
	country: z.string(),
	isValidated: z.boolean().optional(),
	legacyAddressId: z.number().optional(),
	details: z.array(addressBookContactDetailUpdateSchema).optional().default([]),
});

export const addressBookUpdateResponseV2Schema = z.union([
	z.void(),
	z.null(),
	z.object({}).passthrough(),
]);

export type AddressBookUpdateBodyV2 = z.infer<
	typeof addressBookUpdateBodyV2Schema
>;

export type AddressBookContactDetailUpdate = z.infer<
	typeof addressBookContactDetailUpdateSchema
>;

export type AddressBookItemWithFavorite = AddressBookItemSummary & {
	isFavorite: boolean;
	isPickupDefault: boolean;
	isDeliveryDefault: boolean;
	fullAddress?: string;
	pickupContactName?: string;
	deliveryContactName?: string;
};
